import React, { useMemo, useState } from 'react';
import { MdSearch, MdExtension, MdInfoOutline, MdLaunch, MdAutoFixHigh, MdCheck, MdContentCopy } from 'react-icons/md';

const pageGridStyle = {
  display: 'grid',
  gridTemplateColumns: 'minmax(0, 1.25fr) minmax(320px, 0.75fr)',
  gap: '24px',
  alignItems: 'start',
  marginBottom: '20px'
};

const stackedCardStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: '24px'
};

const nestedPanelStyle = {
  padding: '16px',
  marginBottom: '20px',
  background: 'rgba(30, 41, 59, 0.35)'
};

const searchActionRowStyle = {
  display: 'grid',
  gridTemplateColumns: 'auto minmax(0, 1fr)',
  gap: '12px',
  alignItems: 'center'
};

const resultListStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
  maxHeight: '420px',
  overflowY: 'auto',
  paddingRight: '4px'
};

const resultHeaderWithReasonStyle = {
  display: 'grid',
  gridTemplateColumns: 'minmax(0, 1.35fr) minmax(150px, 0.65fr) auto',
  gap: '16px',
  alignItems: 'start',
  marginBottom: '12px'
};

const resultHeaderSimpleStyle = {
  display: 'grid',
  gridTemplateColumns: 'minmax(0, 1fr) auto',
  gap: '16px',
  alignItems: 'start',
  marginBottom: '12px'
};

const resultActionsStyle = {
  display: 'flex',
  gap: '8px',
  flexWrap: 'wrap',
  justifyContent: 'flex-end'
};

const actionButtonStyle = {
  fontSize: '11px',
  fontWeight: '600',
  height: '30px',
  minWidth: '118px'
};

const sharedResultsCardStyle = {
  marginTop: '24px'
};

const RECOMMENDED_EXTENSIONS = [
  {
    name: 'SelectorsHub',
    desc: 'The most advanced XPath & CSS selector tool. Handles Shadow DOM, iframes, and dynamic elements.',
    url: 'https://selectorshub.com/',
    icon: '🚀'
  },
  {
    name: 'XPath Helper',
    desc: 'Simple and clean extension to edit, view and pick relevant XPath queries on any webpage.',
    url: 'https://chrome.google.com/webstore/detail/xpath-helper/hgbbnnohmjbcljjnclbmhciedbknoide',
    icon: '🔍'
  },
  {
    name: 'SelectorsHub Pro',
    desc: 'Premium version with even more advanced locator strategies and automation features.',
    url: 'https://selectorshub.com/selectorshub-pro/',
    icon: '💎'
  }
];

const ADDONS_GUIDE_STEPS = [
  {
    title: 'Paste HTML Snippet',
    desc: 'Paste the HTML content you want to inspect. The XPath tools work only against the snippet currently shown in the HTML Content box.',
    color: '#3b82f6'
  },
  {
    title: 'Generate All XPaths',
    desc: 'Click "Generate Smart XPaths" to list XPaths for all elements from that HTML snippet. This replaces any previous targeted results.',
    color: '#10b981'
  },
  {
    title: 'Find Exact Target',
    desc: 'Use Specific Field / HTML Search with an exact value like button text, id, name, or placeholder, then click "Find Target XPath" to show only exact matching elements.',
    color: '#f59e0b'
  },
  {
    title: 'Validate Or Copy',
    desc: 'Use "Try in Tester" to validate the latest XPath immediately, or copy it for use in your automation steps. Only the last generated result set stays visible.',
    color: '#8b5cf6'
  }
];

function AutomationAddonsQuickStartGuide() {
  const [open, setOpen] = useState(false);

  return (
    <div className="card" style={{ marginBottom: '16px', padding: '14px 18px', border: '1px solid var(--border)' }}>
      <div
        onClick={() => setOpen((value) => !value)}
        style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', userSelect: 'none' }}
      >
        <MdInfoOutline color="var(--primary)" size={18} />
        <strong style={{ fontSize: '14px' }}>Automation Addons — Quick Start Guide</strong>
        <span style={{ marginLeft: 'auto', fontSize: '12px', color: 'var(--text-muted)' }}>{open ? 'Hide' : 'Show'}</span>
      </div>
      {open && (
        <div style={{ marginTop: '14px', display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: '12px' }}>
          {ADDONS_GUIDE_STEPS.map((step, index) => (
            <div
              key={step.title}
              style={{
                display: 'flex',
                gap: '12px',
                alignItems: 'flex-start',
                background: 'var(--bg-input)',
                borderLeft: `4px solid ${step.color}`,
                borderRadius: '8px',
                padding: '12px'
              }}
            >
              <div
                style={{
                  minWidth: '24px',
                  height: '24px',
                  borderRadius: '50%',
                  backgroundColor: step.color,
                  color: '#fff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 700,
                  fontSize: '12px',
                  flexShrink: 0
                }}
              >
                {index + 1}
              </div>
              <div>
                <strong style={{ display: 'block', fontSize: '13px', marginBottom: '4px' }}>{step.title}</strong>
                <span style={{ fontSize: '12px', color: 'var(--text-muted)', lineHeight: 1.5 }}>{step.desc}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function normalizeSpace(value) {
  return String(value || '').replace(/\s+/g, ' ').trim().toLowerCase();
}

function getElementPreview(el) {
  const html = (el.outerHTML || '').trim();
  return html.length > 220 ? `${html.slice(0, 220)}...` : html;
}

function buildXpathLiteral(value) {
  const text = String(value || '');
  if (!text.includes('"')) {
    return `"${text}"`;
  }
  if (!text.includes("'")) {
    return `'${text}'`;
  }
  const parts = text.split('"');
  return `concat(${parts.map((part, index) => `${index > 0 ? ', \'"\', ' : ''}"${part}"`).join('')})`;
}

function getSiblingIndex(el) {
  let index = 1;
  for (let sibling = el.previousElementSibling; sibling; sibling = sibling.previousElementSibling) {
    if (sibling.tagName === el.tagName) {
      index++;
    }
  }
  return index;
}

function hasSiblingWithSameTag(el) {
  if (!el.parentElement) return false;
  return Array.from(el.parentElement.children).filter((child) => child.tagName === el.tagName).length > 1;
}

function getStructuralXPath(el) {
  let path = '';
  for (let current = el; current && current.nodeType === 1; current = current.parentNode) {
    const tagName = current.tagName.toLowerCase();
    const index = getSiblingIndex(current);
    const needsIndex = hasSiblingWithSameTag(current);
    path = `/${tagName}${needsIndex ? `[${index}]` : ''}${path}`;
    if (tagName === 'body' || tagName === 'html') break;
  }
  return path || `//${el.tagName.toLowerCase()}`;
}

function getSmartXPath(el) {
  if (el.id) return `//*[@id=${buildXpathLiteral(el.id)}]`;
  if (el.getAttribute('name')) return `//${el.tagName.toLowerCase()}[@name=${buildXpathLiteral(el.getAttribute('name'))}]`;
  if (el.getAttribute('placeholder')) return `//${el.tagName.toLowerCase()}[@placeholder=${buildXpathLiteral(el.getAttribute('placeholder'))}]`;
  if (el.getAttribute('for')) return `//${el.tagName.toLowerCase()}[@for=${buildXpathLiteral(el.getAttribute('for'))}]`;
  if (el.getAttribute('href')) return `//${el.tagName.toLowerCase()}[@href=${buildXpathLiteral(el.getAttribute('href'))}]`;

  const ownText = Array.from(el.childNodes)
    .filter((node) => node.nodeType === Node.TEXT_NODE)
    .map((node) => node.textContent)
    .join(' ')
    .replace(/\s+/g, ' ')
    .trim();
  const tagName = el.tagName.toLowerCase();
  const allowTextXPath = ['button', 'a', 'label', 'span', 'option', 'td', 'th'].includes(tagName);

  if (allowTextXPath && ownText && ownText.length < 60) {
    return `//${tagName}[normalize-space()=${buildXpathLiteral(ownText)}]`;
  }

  const className = (el.getAttribute('class') || '').trim().split(/\s+/).filter(Boolean);
  if (className.length === 1 && className[0].length < 40) {
    return `//${tagName}[contains(@class, ${buildXpathLiteral(className[0])})]`;
  }

  const title = el.getAttribute('title');
  if (title) {
    return `//${tagName}[@title=${buildXpathLiteral(title)}]`;
  }

  const ariaLabel = el.getAttribute('aria-label');
  if (ariaLabel) {
    return `//${tagName}[@aria-label=${buildXpathLiteral(ariaLabel)}]`;
  }

  const value = el.getAttribute('value');
  if (value) {
    return `//${tagName}[@value=${buildXpathLiteral(value)}]`;
  }

  const role = el.getAttribute('role');
  if (role) {
    return `//${tagName}[@role=${buildXpathLiteral(role)}]`;
  }

  if (ownText && ownText.length < 60 && el.children.length === 0) {
    return `//${el.tagName.toLowerCase()}[normalize-space()=${buildXpathLiteral(ownText)}]`;
  }

  return getStructuralXPath(el);
}

function describeElement(el, index) {
  const tagName = el.tagName.toLowerCase();
  const type = el.getAttribute('type') || '';
  const identifier = el.id || el.getAttribute('name') || el.getAttribute('placeholder') || el.textContent.trim().substring(0, 30) || `Element ${index + 1}`;

  return {
    id: `${tagName}-${index}`,
    tag: tagName,
    type,
    displayName: `${tagName.toUpperCase()}${type ? ` (${type})` : ''}: ${identifier}`,
    xpath: getSmartXPath(el),
    preview: getElementPreview(el)
  };
}

function isUsefulMatch(el, target) {
  const attributes = ['id', 'name', 'placeholder', 'for', 'href', 'type', 'value', 'class', 'title', 'aria-label'];
  const attrHit = attributes.find((attr) => normalizeSpace(el.getAttribute(attr)) === target);
  const ownText = normalizeSpace(
    Array.from(el.childNodes)
      .filter((node) => node.nodeType === Node.TEXT_NODE)
      .map((node) => node.textContent)
      .join(' ')
  );
  const textHit = ownText === target;
  const exactHtmlHit = normalizeSpace(el.outerHTML) === target;

  if (attrHit) {
    return { matched: true, reason: `Matched ${attrHit} attribute`, score: 4 };
  }
  if (textHit) {
    return { matched: true, reason: 'Matched text content', score: 3 };
  }
  if (exactHtmlHit) {
    return { matched: true, reason: 'Matched exact HTML content', score: 5 };
  }

  return { matched: false, reason: '', score: 0 };
}

function findTargetedMatches(doc, searchTerm) {
  const target = normalizeSpace(searchTerm);
  if (!target) return [];

  const elements = Array.from(doc.querySelectorAll('body *'));
  const matched = elements.reduce((matches, el, index) => {
    const result = isUsefulMatch(el, target);
    if (!result.matched) {
      return matches;
    }

    matches.push({
      ...describeElement(el, index),
      matchReason: result.reason,
      matchScore: result.score
    });
    return matches;
  }, []);

  const filtered = matched.filter((item) => {
    const itemXPath = item.xpath;
    return !matched.some((other) => {
      if (other.id === item.id || other.matchScore < item.matchScore) {
        return false;
      }
      return other.preview !== item.preview && other.preview.includes(item.preview) && other.xpath !== itemXPath;
    });
  });

  return filtered.sort((a, b) => b.matchScore - a.matchScore || a.displayName.localeCompare(b.displayName));
}

function Addons() {
  const [htmlSnippet, setHtmlSnippet] = useState('<div class="container">\n  <label for="username">Username:</label>\n  <input id="username" name="user_login" placeholder="Enter username" />\n  <button type="submit" class="btn-primary">Login Now</button>\n  <a href="/forgot">Forgot Password?</a>\n</div>');
  const [xpath, setXpath] = useState('//button[text()="Login Now"]');
  const [searchTarget, setSearchTarget] = useState('username');
  const [testResult, setTestResult] = useState(null);
  const [generatedXpaths, setGeneratedXpaths] = useState([]);
  const [targetedXpaths, setTargetedXpaths] = useState([]);
  const [lastGeneratedType, setLastGeneratedType] = useState('');
  const [finderError, setFinderError] = useState('');
  const [copiedId, setCopiedId] = useState(null);

  const targetedSummary = useMemo(() => {
    if (!searchTarget.trim()) {
      return 'Search by field name, id, placeholder, text, or pasted HTML fragment.';
    }
    return `Found ${targetedXpaths.length} matching element${targetedXpaths.length === 1 ? '' : 's'} for "${searchTarget.trim()}".`;
  }, [searchTarget, targetedXpaths.length]);

  const testXpath = () => {
    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(htmlSnippet, 'text/html');
      const result = doc.evaluate(xpath, doc, null, XPathResult.ANY_TYPE, null);

      const node = result.iterateNext();
      if (node) {
        setTestResult({
          success: true,
          message: 'Match Found!',
          content: node.outerHTML || node.textContent
        });
      } else {
        setTestResult({
          success: false,
          message: 'No matches found.'
        });
      }
    } catch (err) {
      setTestResult({
        success: false,
        message: `Invalid XPath or HTML: ${err.message}`
      });
    }
  };

  const generateSmartXPaths = () => {
    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(htmlSnippet, 'text/html');
      const allElements = doc.querySelectorAll('body *');

      const results = Array.from(allElements).map((el, index) => describeElement(el, index));
      setGeneratedXpaths(results);
      setTargetedXpaths([]);
      setLastGeneratedType('all');
      setFinderError('');
    } catch (err) {
      setGeneratedXpaths([]);
      setTargetedXpaths([]);
      setLastGeneratedType('');
      setFinderError(`Error generating XPaths: ${err.message}`);
    }
  };

  const findSpecificXPath = () => {
    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(htmlSnippet, 'text/html');
      const results = findTargetedMatches(doc, searchTarget);

      setGeneratedXpaths([]);
      setTargetedXpaths(results);
      setLastGeneratedType('targeted');
      setFinderError(results.length ? '' : 'No matching field or HTML content found for your search.');
    } catch (err) {
      setGeneratedXpaths([]);
      setTargetedXpaths([]);
      setLastGeneratedType('');
      setFinderError(`Error searching target field: ${err.message}`);
    }
  };

  const copyToClipboard = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const handleTryInTester = (text) => {
    setXpath(text);
    setTestResult(null);
  };

  const activeResults = lastGeneratedType === 'targeted' ? targetedXpaths : generatedXpaths;
  const activeResultsTitle = lastGeneratedType === 'targeted'
    ? `Targeted Matches (${targetedXpaths.length})`
    : `All Elements (${generatedXpaths.length})`;
  const activeResultsShowReason = lastGeneratedType === 'targeted';

  const renderResultList = (results, title, showReason = false) => (
    <div>
      <h3 style={{ marginBottom: '16px', fontSize: '18px' }}>{title}</h3>
      <div style={resultListStyle}>
        {results.map((res) => (
          <div
            key={res.id}
            style={{
              padding: '14px',
              borderRadius: '10px',
              background: 'var(--bg-input)',
              border: '1px solid var(--border)',
              fontSize: '12px'
            }}
          >
            <div style={showReason ? resultHeaderWithReasonStyle : resultHeaderSimpleStyle}>
              <div>
                <div style={{ fontWeight: 'bold', color: 'var(--primary)', marginBottom: '6px', lineHeight: 1.35 }}>{res.displayName}</div>
                {res.preview && (
                  <pre style={{ margin: 0, fontSize: '11px', color: 'var(--text-muted)', lineHeight: 1.55, whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontFamily: 'inherit' }}>
                    {res.preview}
                  </pre>
                )}
              </div>
              {showReason && (
                <div style={{ fontSize: '11px', color: 'var(--text-muted)', minWidth: 0 }}>
                  <strong style={{ color: 'var(--text-main)', display: 'block', marginBottom: '4px' }}>Match Type</strong>
                  {res.matchReason}
                </div>
              )}
              <div style={resultActionsStyle}>
                <button
                  className="btn btn-sm btn-primary"
                  onClick={() => handleTryInTester(res.xpath)}
                  style={actionButtonStyle}
                >
                  Try in Tester
                </button>
                <button
                  className="btn btn-sm btn-secondary"
                  onClick={() => copyToClipboard(res.xpath, res.id)}
                  style={{
                    ...actionButtonStyle,
                    color: copiedId === res.id ? 'var(--success)' : 'var(--text-main)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px'
                  }}
                >
                  {copiedId === res.id ? <><MdCheck size={14} /> Copied!</> : <><MdContentCopy size={14} /> Copy XPath</>}
                </button>
              </div>
            </div>
            <code style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.2)', padding: '8px 10px', borderRadius: '6px', overflowX: 'auto', border: '1px solid var(--border)' }}>
              {res.xpath}
            </code>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div className="addons-page">
      <AutomationAddonsQuickStartGuide />

      <div style={pageGridStyle}>
        <div className="card" style={{ display: 'flex', flexDirection: 'column' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
            <MdAutoFixHigh color="var(--primary)" size={20} />
            <h3 style={{ margin: 0 }}>Smart XPath Creator</h3>
          </div>
          <p style={{ fontSize: '13px', color: 'var(--text-muted)', marginBottom: '16px' }}>
            Paste an HTML snippet below and generate XPaths for all elements in that HTML, or search for one exact field/content and get only that matching XPath.
          </p>

          <div className="form-group">
            <label>HTML Content</label>
            <textarea
              value={htmlSnippet}
              onChange={(e) => setHtmlSnippet(e.target.value)}
              style={{ height: '140px', fontFamily: '"Fira Code", monospace', fontSize: '12px' }}
              placeholder="Paste HTML here..."
            />
          </div>

          <div className="card" style={nestedPanelStyle}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
              <MdSearch color="var(--primary)" size={18} />
              <h4 style={{ margin: 0 }}>Specific Field / HTML Search</h4>
            </div>
            <p style={{ margin: '0 0 12px 0', fontSize: '12px', color: 'var(--text-muted)', lineHeight: 1.5 }}>
              Enter an exact field label, id, name, placeholder, button text, or full HTML fragment to get only the exact matching XPath from the HTML content.
            </p>
            <div className="form-group" style={{ marginBottom: '12px' }}>
              <label>Search Target</label>
              <input
                type="text"
                value={searchTarget}
                onChange={(e) => setSearchTarget(e.target.value)}
                placeholder='Example: Login Now, username, Enter username'
              />
            </div>
            <div style={searchActionRowStyle}>
              <button className="btn btn-primary" onClick={findSpecificXPath} style={{ minWidth: '180px' }}>
                Find Target XPath
              </button>
              <span style={{ fontSize: '12px', color: 'var(--text-muted)', lineHeight: 1.5 }}>{targetedSummary}</span>
            </div>
          </div>

          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            <button
              className="btn btn-success"
              style={{ width: '100%', padding: '12px', fontWeight: 'bold' }}
              onClick={generateSmartXPaths}
            >
              Generate Smart XPaths
            </button>
          </div>

          {finderError && <div className="error" style={{ marginTop: '16px' }}>{finderError}</div>}
        </div>

        <div style={stackedCardStyle}>
          <div className="card">
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
              <MdSearch color="var(--primary)" size={20} />
              <h3 style={{ margin: 0 }}>Live XPath Tester</h3>
            </div>

            <div className="form-group">
              <label>XPath Expression</label>
              <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) auto', gap: '10px', alignItems: 'center' }}>
                <input
                  type="text"
                  value={xpath}
                  onChange={(e) => setXpath(e.target.value)}
                  placeholder="//tag[@attr='value']"
                  style={{ fontFamily: '"Fira Code", monospace' }}
                />
                <button className="btn btn-primary" onClick={testXpath} style={{ minWidth: '130px' }}>Validate</button>
              </div>
            </div>

            {testResult && (
              <div
                style={{
                  marginTop: '16px',
                  padding: '12px',
                  borderRadius: '8px',
                  backgroundColor: testResult.success ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)',
                  border: `1px solid ${testResult.success ? 'var(--success)' : 'var(--danger)'}`
                }}
              >
                <div style={{ fontWeight: 'bold', fontSize: '13px', color: testResult.success ? 'var(--success)' : 'var(--danger)', marginBottom: '4px' }}>
                  {testResult.message}
                </div>
                {testResult.content && (
                  <pre style={{ margin: 0, fontSize: '11px', overflowX: 'auto', padding: '8px', backgroundColor: 'rgba(0,0,0,0.2)', borderRadius: '4px' }}>
                    {testResult.content}
                  </pre>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {activeResults.length > 0 && (
        <div className="card" style={sharedResultsCardStyle}>
          {renderResultList(activeResults, activeResultsTitle, activeResultsShowReason)}
        </div>
      )}

      <div className="card" style={{ marginTop: '24px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '20px' }}>
          <MdExtension color="var(--primary)" size={20} />
          <h3 style={{ margin: 0 }}>Curated Browser Extensions</h3>
        </div>
        <div className="responsive-grid-4" style={{ gap: '16px' }}>
          {RECOMMENDED_EXTENSIONS.map((ext, i) => (
            <div
              key={i}
              className="addon-item"
              style={{
                padding: '16px',
                borderRadius: '12px',
                background: 'var(--bg-input)',
                border: '1px solid var(--border)',
                display: 'flex',
                flexDirection: 'column',
                gap: '12px',
                alignItems: 'center',
                textAlign: 'center'
              }}
            >
              <span style={{ fontSize: '32px' }}>{ext.icon}</span>
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <h4 style={{ margin: 0, fontSize: '15px', wordBreak: 'break-all' }}>{ext.name}</h4>
                <p style={{ margin: 0, fontSize: '12px', color: 'var(--text-muted)', lineHeight: '1.4' }}>{ext.desc}</p>
                <div style={{ marginTop: 'auto', paddingTop: '8px' }}>
                  <a
                    href={ext.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-sm btn-secondary"
                    style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}
                  >
                    <MdLaunch /> Visit Site
                  </a>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default Addons;
